import { Crosshair, Grid3x3, MapPin, Route, ShieldAlert } from "lucide-react";
import { type ReactNode, useEffect } from "react";

import { ESCAPE, registerEscape } from "../components/escapeStack";
import { PropRow } from "../components/panel";
import { Badge, Button, FloatingPanel } from "../components/ui";
import { bridgeName, k, t, tServer } from "../i18n/i18n";
import { bridgeSvg } from "../map/bridgeImages";
import { formatMgrs } from "../map/MapCanvas";
import { useBridgeState } from "../map/useBridgeState";
import { type Selection, useUi } from "../state/ui";
import { AnnotationCard } from "./AnnotationCard";
import { CardActions, CardFooter, CardTitle } from "./cardParts";
import { chokepointReason } from "./chokepoint";
import { GraphicCard } from "./GraphicCard";
import { LineCard } from "./LineCard";
import { MeasureCard } from "./MeasureCard";
import { SubUnitCard } from "./SubUnitCard";
import { UnitCard } from "./UnitCard";

/** Fiche d'objet (panneau droit), à la manière de Prevail : en-tête à icône, propriétés, pastilles d'état (R6 :
 *  `Badge dot`, réservé à l'exception), actions dans le pied. */

const SEVERITY: Record<string, { label: string; tone: "warn" | "neutral" }> = {
  HIGH: { label: k("Élevée"), tone: "warn" },
  MEDIUM: { label: k("Moyenne"), tone: "neutral" },
  LOW: { label: k("Faible"), tone: "neutral" },
};

const ROAD_CLASS: Record<string, string> = {
  motorway: k("Autoroute"),
  trunk: k("Voie rapide"),
  primary: k("Route principale"),
  secondary: k("Route secondaire"),
  tertiary: k("Route tertiaire"),
};

function asRecord(value: unknown): Record<string, unknown> {
  if (typeof value === "string") {
    try {
      return JSON.parse(value) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return (value as Record<string, unknown>) ?? {};
}

/** Texte venu des données de la carte (motif d'un point de passage obligé, source d'un relevé, classe de route) :
 *  traduit par le dictionnaire du serveur quand il y figure. */
const fromData = (value: unknown) => (value === undefined || value === null || value === "" ? "—" : tServer(String(value)));

/** Fiche de l'objet sélectionné (`selection`, passée par la coque : la fiche garde son contenu pendant sa sortie en
 *  glissé). Unité, ligne et graphique ont leur propre fiche. */
export function Inspector({ selection }: { selection: Selection }) {
  const requestFlyTo = useUi((s) => s.requestFlyTo);
  const mapSlug = useUi((s) => s.mapSlug);
  const bridgeState = useBridgeState();
  // Échap (pile partagée) ferme la fiche, après un menu, un tracé ou la sélection d'un sommet ; un tracé en cours
  // garde la main.
  useEffect(
    () =>
      registerEscape(ESCAPE.card, () => {
        const ui = useUi.getState();
        if (ui.draft || !ui.selection) return false;
        ui.select(null);
        return true;
      }),
    [],
  );
  if (selection.layer === "unit") return <UnitCard code={selection.code} />;
  if (selection.layer === "subunit") return <SubUnitCard unitKey={selection.key} />;
  if (selection.layer === "line") return <LineCard name={selection.name} />;
  if (selection.layer === "graphic") return <GraphicCard id={selection.id} />;
  if (selection.layer === "annotation") return <AnnotationCard id={selection.id} />;
  // Ligne de la ZO (docs/15 §4.4) : fiche propre, au temps affiché pour la FLOT et la CFL.
  if (selection.layer === "measure") return <MeasureCard measureKey={selection.key} />;

  const p = selection.properties;
  const mgrs = formatMgrs(selection.lngLat[0], selection.lngLat[1]);
  let icon: ReactNode = <MapPin size={16} />;
  let title = t("Objet");
  let subtitle = "";
  let body: ReactNode = null;
  let action: ReactNode = null;

  switch (selection.layer) {
    case "grid": {
      icon = <Grid3x3 size={16} />;
      title = String(p.locality || t("Case sans toponyme"));
      subtitle = t("Case {n}", { n: String(p.square) });
      body = (
        <>
          <PropRow label={t("Rangée / colonne")}>
            {String(p.row)} / {String(p.col)}
          </PropRow>
          <PropRow label={t("Placement")}>
            {p.valid ? (
              <Badge dot tone="ok">
                {t("Autorisé")}
              </Badge>
            ) : (
              <Badge dot tone="warn">
                {t("Case exclue")}
              </Badge>
            )}
          </PropRow>
        </>
      );
      break;
    }
    case "bridges": {
      const g = asRecord(p.gabarits);
      const bridgeKey = typeof p.key === "string" ? p.key : null;
      const destroyed = bridgeKey !== null && bridgeState.destroyed.includes(bridgeKey);
      icon = <span className="flex [&>svg]:block" dangerouslySetInnerHTML={{ __html: bridgeSvg(destroyed, 22) }} />;
      title = bridgeName(p.name == null ? null : String(p.name), Boolean(mapSlug?.startsWith("zone-")));
      subtitle = t("Pont");
      body = (
        <>
          <PropRow label={t("État")}>
            {destroyed ? (
              <Badge dot tone="danger">
                {t("Détruit")}
              </Badge>
            ) : (
              <Badge dot tone="ok">
                {t("Intact")}
              </Badge>
            )}
          </PropRow>
          <PropRow label={t("Poids maximal")}>{g.poids_maximal_autorise_t ? `${String(g.poids_maximal_autorise_t)} t` : "—"}</PropRow>
          <PropRow label={t("Hauteur libre")}>{g.hauteur_libre_m ? `${String(g.hauteur_libre_m)} m` : "—"}</PropRow>
          <PropRow label={t("Longueur / largeur")}>
            {g.longueur_m ? `${String(g.longueur_m)} m` : "—"} / {g.largeur_m ? `${String(g.largeur_m)} m` : "—"}
          </PropRow>
          <PropRow label={t("Source des gabarits")}>
            {p.source === "default" ? (
              <Badge dot tone="warn">
                {t("Valeurs par défaut")}
              </Badge>
            ) : p.source === "mixed" ? (
              t("Relevé partiel")
            ) : (
              fromData(p.source)
            )}
          </PropRow>
        </>
      );
      if (bridgeKey)
        action = (
          <Button
            variant="secondary"
            size="sm"
            disabled={bridgeState.busy}
            onClick={() => bridgeState.toggle({ key: bridgeKey, name: title })}
          >
            {destroyed ? t("Rétablir le pont") : t("Détruire le pont")}
          </Button>
        );
      break;
    }
    case "chokepoints": {
      const severity = SEVERITY[String(p.severity)];
      icon = <ShieldAlert size={16} />;
      title = p.kind === "bridge" ? t("Pont") : t("Carrefour");
      subtitle = t("Point de passage obligé");
      body = (
        <>
          <PropRow label={t("Sévérité")}>
            {severity ? (
              severity.tone === "warn" ? (
                <Badge dot tone="warn">
                  {t(severity.label)}
                </Badge>
              ) : (
                t(severity.label)
              )
            ) : (
              fromData(p.severity)
            )}
          </PropRow>
          <PropRow label={t("Motif")}>{chokepointReason(p.reason, p.kind)}</PropRow>
        </>
      );
      break;
    }
    case "roads": {
      icon = <Route size={16} />;
      const road = ROAD_CLASS[String(p.class)];
      title = road ? t(road) : p.class ? fromData(p.class) : t("Route");
      subtitle = t("Réseau routier");
      break;
    }
    default:
      body = null;
  }

  return (
    <FloatingPanel
      enter="right"
      width="card"
      tallHeader={Boolean(subtitle)}
      icon={icon}
      title={<CardTitle title={title} subtitle={subtitle} />}
      actions={<CardActions />}
      footer={
        <CardFooter>
          <Button variant="secondary" size="sm" onClick={() => requestFlyTo(selection.lngLat, 13)}>
            <Crosshair size={14} /> {t("Centrer")}
          </Button>
          {action}
        </CardFooter>
      }
    >
      <div className="px-4 py-2">
        {body}
        <PropRow label="MGRS" mono>
          {mgrs}
        </PropRow>
        <PropRow label={t("Lat / Lon")} mono>
          {selection.lngLat[1].toFixed(5)} / {selection.lngLat[0].toFixed(5)}
        </PropRow>
      </div>
    </FloatingPanel>
  );
}
